import { PriorityPlaylist } from '@backrs_ui/common-ui';

import { createApi } from '@reduxjs/toolkit/query/react';

import {
  PriorityPlaylistPost,
  PriorityTask,
  PriorityTaskPost,
  PriorityTaskStatusType,
  PriorityTaskType,
} from './priorities.types';
import { baseQueryWithAuth } from '../baseQuery';

export type LinkPreviewResponse = Readonly<{
  url: string;
  title: string;
  siteName: string;
  description: string;
  mediaType: string;
  contentType: string;
  images: string[];
  videos: string[];
  favicons: string[];
}>;

export const prioritiesApi = createApi({
  reducerPath: 'prioritiesApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: [
    'UnassignedPriorityTasks',
    'PriorityPlaylists',
    'PriorityTasks',
    'PrioritySuggestedPlaylists',
    'PrioritySuggestedTasks',
  ],
  endpoints: (build) => ({
    /**
     * TASKS
     */
    getUnassignedPriorityTasks: build.query<PriorityTask[], undefined>({
      query: () => ({
        url: 'priorities/tasks',
        params: {
          filter: JSON.stringify({ playlist_id: null }),
        },
      }),
      keepUnusedDataFor: 60 * 60 * 24,
      providesTags: [{ type: 'UnassignedPriorityTasks' }],
    }),

    postPriorityTask: build.mutation<PriorityTask, Partial<PriorityTaskPost>>({
      query: (task) => ({
        url: 'priorities/tasks',
        method: 'POST',
        body: task,
      }),
      invalidatesTags: [
        { type: 'UnassignedPriorityTasks' },
        { type: 'PriorityPlaylists' },
        { type: 'PriorityTasks' },
      ],
    }),

    putPriorityTask: build.mutation<PriorityTask, Partial<PriorityTaskPost> & { id: number }>({
      query: (task) => ({
        url: `priorities/tasks/${task.id}`,
        method: 'PUT',
        body: task,
      }),
      invalidatesTags: [
        { type: 'UnassignedPriorityTasks' },
        { type: 'PriorityPlaylists' },
        { type: 'PriorityTasks' },
      ],
    }),

    deletePriorityTask: build.mutation<unknown, number>({
      query: (id) => ({
        url: `priorities/tasks/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        { type: 'PriorityPlaylists' },
        { type: 'UnassignedPriorityTasks' },
        { type: 'PriorityTasks' },
      ],
    }),

    getStatusTypes: build.query<PriorityTaskStatusType[], undefined>({
      query: () => 'priorities/tasks/statuses',
      keepUnusedDataFor: 60 * 60 * 24 * 7, // Cache for a week
    }),

    getTaskTypes: build.query<PriorityTaskType[], undefined>({
      query: () => 'priorities/tasks/types',
      keepUnusedDataFor: 60 * 60 * 24 * 7, // Cache for a week
    }),

    /**
     * PLAYLISTS
     */
    getPlaylists: build.query<PriorityPlaylist[], undefined>({
      query: () => 'priorities/playlists',
      keepUnusedDataFor: 60 * 60 * 24 * 7, // Cache for a week
      providesTags: [{ type: 'PriorityPlaylists' }],
    }),

    getPlaylistById: build.query<PriorityPlaylist, { id: number; isAdminRequest?: boolean }>({
      query: ({ id, isAdminRequest }) => ({
        url: `priorities/playlists/${id}`,
        params: isAdminRequest ? { admin: 'true' } : undefined,
      }),
      keepUnusedDataFor: 60 * 60 * 24 * 7, // Cache for a week
      providesTags: [{ type: 'PriorityPlaylists' }],
    }),

    getSuggestedPlaylists: build.query<PriorityPlaylist[], undefined>({
      query: () => 'priorities/playlists/suggested',
      keepUnusedDataFor: 60 * 60 * 24 * 7, // Cache for a week
      providesTags: [{ type: 'PrioritySuggestedPlaylists' }],
    }),

    getSuggestedPlaylistById: build.query<PriorityPlaylist, number>({
      query: (id) => `priorities/playlists/suggested/${id}`,
      keepUnusedDataFor: 60 * 60 * 24 * 7, // Cache for a week
      providesTags: [{ type: 'PrioritySuggestedPlaylists' }],
    }),

    getSuggestedPlaylistBySlug: build.query<PriorityPlaylist, string>({
      query: (slug) => `priorities/playlists/suggested/slug/${slug}`,
      keepUnusedDataFor: 60 * 60 * 24 * 7, // Cache for a week
      providesTags: [{ type: 'PrioritySuggestedPlaylists' }],
    }),

    addSuggestedPlaylistById: build.mutation<PriorityPlaylist, number>({
      query: (id) => ({
        url: `priorities/playlists/suggested/add/${id}`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'PrioritySuggestedPlaylists' }, { type: 'PriorityPlaylists' }],
    }),
    getLinkPreview: build.query<LinkPreviewResponse, string>({
      query: (url) => {
        const normalizedUrl =
          url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;

        return {
          url: 'posts/preview',
          params: { href: normalizedUrl },
        };
      },
      keepUnusedDataFor: 60 * 60 * 24, // Cache for 24 hours
    }),

    getTasksForPlaylist: build.query<PriorityTask[], number>({
      query: (playlistId) => ({
        url: `priorities/playlists/${playlistId}/tasks`,
      }),
      keepUnusedDataFor: 60 * 60 * 24,
      providesTags: [{ type: 'PriorityTasks' }],
    }),

    getTasksForSuggestedPlaylist: build.query<PriorityTask[], number>({
      query: (playlistId) => ({
        url: `priorities/playlists/suggested/${playlistId}/tasks`,
      }),
      keepUnusedDataFor: 60 * 60 * 24,
      providesTags: [{ type: 'PrioritySuggestedTasks' }],
    }),

    postPlaylist: build.mutation<PriorityPlaylist, PriorityPlaylistPost>({
      query: (playlist) => ({
        url: 'priorities/playlists',
        method: 'POST',
        body: playlist,
      }),
      invalidatesTags: [{ type: 'PriorityPlaylists' }],
    }),

    putPlaylist: build.mutation<PriorityPlaylist, PriorityPlaylistPost & { id: number }>({
      query: (playlist) => ({
        url: `priorities/playlists/${playlist.id}`,
        method: 'PUT',
        body: playlist,
      }),
      invalidatesTags: [{ type: 'PriorityPlaylists' }],
    }),

    deletePlaylist: build.mutation<unknown, number>({
      query: (id) => ({
        url: `priorities/playlists/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'PriorityPlaylists' }, { type: 'UnassignedPriorityTasks' }],
    }),

    /**
     * Update task order in a playlist
     */
    updateTaskOrder: build.mutation<PriorityTask[], { playlistId: number; taskIds: number[] }>({
      query: ({ playlistId, taskIds }) => ({
        url: `priorities/playlists/${playlistId}/tasks/order`,
        method: 'PUT',
        body: { task_ids: taskIds },
      }),
      invalidatesTags: [{ type: 'PriorityTasks' }],
    }),
  }),
});

export const {
  useGetUnassignedPriorityTasksQuery,
  usePostPriorityTaskMutation,
  usePutPriorityTaskMutation,
  useDeletePriorityTaskMutation,
  useGetStatusTypesQuery,
  useGetTaskTypesQuery,
  useGetTasksForPlaylistQuery,
  useGetTasksForSuggestedPlaylistQuery,

  useGetPlaylistByIdQuery,
  useGetPlaylistsQuery,
  useGetSuggestedPlaylistsQuery,
  useGetSuggestedPlaylistByIdQuery,
  useGetSuggestedPlaylistBySlugQuery,
  useAddSuggestedPlaylistByIdMutation,
  usePostPlaylistMutation,
  usePutPlaylistMutation,
  useDeletePlaylistMutation,
  useUpdateTaskOrderMutation,
  useGetLinkPreviewQuery,

  util: { resetApiState },
} = prioritiesApi;
